import api from '../api';

/**
 * Checks if a URL is likely to be an S3 URL (direct or proxied)
 * @param url The URL to check
 * @returns True if the URL appears to be an S3 URL
 */
export const isLikelyS3Url = (url: string): boolean => {
  try {
    // Check for common S3 signatures in the URL
    return (
      url.includes('amazonaws.com') || // Direct S3 URL
      url.includes('X-Amz-Signature') || // Has S3 signature parameters
      url.includes('X-Amz-Algorithm') || // Has S3 algorithm parameters
      url.includes('X-Amz-Credential') || // Has S3 credential parameters
      url.includes('X-Amz-Date') // Has S3 date parameters
    );
  } catch (error) {
    return false;
  }
};

/**
 * Refreshes an expired pre-signed URL for a stored document record.
 * Audit 30A: the old arbitrary-key POST /documents/refresh-url oracle was
 * removed — the server now authorises the caller against the document's
 * parent resource and presigns the stored key.
 * @param documentId The Document record id
 * @returns A fresh pre-signed URL
 */
export const refreshDocumentUrl = async (documentId: string): Promise<string> => {
  try {
    const response = await api.post(`/documents/${documentId}/refresh-url`);

    if (response.data && response.data.success && response.data.data?.url) {
      return response.data.data.url;
    }

    throw new Error('Failed to refresh URL');
  } catch (error) {
    console.error('Error refreshing document URL:', error);
    throw error;
  }
};

/**
 * Checks if a PDF URL is likely expired based on common error patterns
 * @param url The URL to check
 * @returns True if the URL appears to be expired
 */
export const isPdfUrlLikelyExpired = (url: string): boolean => {
  // If the URL contains S3 signatures, check if it's likely expired
  if (url.includes('X-Amz-Date') && url.includes('X-Amz-Expires')) {
    try {
      // Try to extract expiration time from URL
      const urlObj = new URL(url);
      const expiresParam = urlObj.searchParams.get('X-Amz-Expires');
      const dateParam = urlObj.searchParams.get('X-Amz-Date');
      
      if (expiresParam && dateParam) {
        // Parse the AWS date format (YYYYMMDDTHHMMSSZ)
        const year = parseInt(dateParam.substring(0, 4));
        const month = parseInt(dateParam.substring(4, 6)) - 1; // JS months are 0-indexed
        const day = parseInt(dateParam.substring(6, 8));
        const hour = parseInt(dateParam.substring(9, 11));
        const minute = parseInt(dateParam.substring(11, 13));
        const second = parseInt(dateParam.substring(13, 15));
        
        const signedDate = new Date(Date.UTC(year, month, day, hour, minute, second));
        const expiresInSeconds = parseInt(expiresParam);
        const expirationDate = new Date(signedDate.getTime() + expiresInSeconds * 1000);
        
        // If URL expires in less than 5 minutes or has already expired
        const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000);
        return expirationDate <= fiveMinutesFromNow;
      }
    } catch (error) {
      console.warn('Error checking URL expiration:', error);
    }
  }
  
  return false;
};
